import { Box, Divider, HStack, Link, Text, VStack } from "@chakra-ui/react";

const DetailRow = ({ label, value, mono = false }) => {
  if (value === null || value === undefined || value === "" || value === "N/A")
    return null;
  return (
    <HStack justifyContent="space-between" fontSize="13px" align="flex-start">
      <Text color="gray.500" flexShrink={0} mr="8px">
        {label}
      </Text>
      <Text
        fontWeight="600"
        textAlign="right"
        fontFamily={mono ? "mono" : undefined}
        wordBreak="break-all"
      >
        {value}
      </Text>
    </HStack>
  );
};

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------
const DetailsSection = ({ title, children }) => (
  <Box w="100%">
    <Divider my="8px" />
    {title && (
      <Text
        fontSize="11px"
        fontWeight="700"
        color="gray.400"
        mb="6px"
        letterSpacing="wider"
        textTransform="uppercase"
      >
        {title}
      </Text>
    )}
    <VStack spacing="6px" align="stretch">
      {children}
    </VStack>
  </Box>
);

// ---------------------------------------------------------------------------
// Per-service renderers
// ---------------------------------------------------------------------------
const ElectricityPrepaid = ({ details }) => (
  <DetailsSection title="Electricity Token">
    <DetailRow label="Token" value={details.token} mono />
    <DetailRow label="Units" value={details.units} />
    <DetailRow
      label="Token Amount"
      value={
        details.tokenAmount
          ? `₦${Number(details.tokenAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
          : null
      }
    />
    <DetailRow label="Tariff" value={details.tariff} />
    <DetailRow label="Meter Number" value={details.meterNumber} />
    <DetailRow label="Customer" value={details.customerName} />
    <DetailRow label="Address" value={details.customerAddress} />
    <DetailRow label="Exchange Ref" value={details.exchangeReference} mono />
  </DetailsSection>
);

const ElectricityPostpaid = ({ details }) => (
  <DetailsSection title="Electricity Bill Payment">
    <DetailRow label="Customer" value={details.customerName} />
    <DetailRow label="Address" value={details.customerAddress} />
    <DetailRow label="Utility" value={details.utilityName} />
    <DetailRow label="Exchange Ref" value={details.exchangeReference} mono />
    <DetailRow
      label="Outstanding Balance"
      value={
        details.balance != null
          ? `₦${Number(details.balance).toLocaleString()}`
          : null
      }
    />
  </DetailsSection>
);

const InsuranceDetails = ({ details }) => (
  <DetailsSection title="Insurance Certificate">
    <DetailRow label="Policy Ref" value={details.uniqueElement} mono />
    {details.certUrl ? (
      <HStack justifyContent="space-between" fontSize="13px">
        <Text color="gray.500" flexShrink={0} mr="8px">
          Certificate
        </Text>
        <Link
          href={details.certUrl}
          isExternal
          color="blue.500"
          fontWeight="600"
          fontSize="13px"
        >
          Download ↗
        </Link>
      </HStack>
    ) : (
      <DetailRow label="Certificate" value={details.purchasedCode} />
    )}
  </DetailsSection>
);

const EducationDetails = ({ details }) => {
  // WAEC result checker — has cards array
  if (details.cards && details.cards.length > 0) {
    return (
      <DetailsSection title="WAEC Result Checker PINs">
        {details.cards.map((card, i) => (
          <Box key={i} p="8px" bg="gray.50" borderRadius="6px">
            <DetailRow label="Serial No." value={card.Serial} mono />
            <DetailRow label="PIN" value={card.Pin} mono />
          </Box>
        ))}
      </DetailsSection>
    );
  }

  // WAEC registration — has tokens array
  if (details.tokens && details.tokens.length > 0) {
    return (
      <DetailsSection title="WAEC Registration Tokens">
        {details.tokens.map((token, i) => (
          <DetailRow
            key={i}
            label={`Token ${details.tokens.length > 1 ? i + 1 : ""}`}
            value={token}
            mono
          />
        ))}
      </DetailsSection>
    );
  }

  // JAMB / single-token education product
  if (details.pin) {
    return (
      <DetailsSection title="Education PIN">
        <DetailRow label="PIN" value={details.pin} mono />
      </DetailsSection>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// Main export — picks the right renderer by `type`
// ---------------------------------------------------------------------------
export const VtuProviderDetails = ({ providerDetails }) => {
  if (!providerDetails) return null;

  const { type, productName, transactionDate, status } = providerDetails;

  const renderContent = () => {
    switch (type) {
      case "ELECTRICITY_PREPAID":
        return <ElectricityPrepaid details={providerDetails} />;
      case "ELECTRICITY_POSTPAID":
        return <ElectricityPostpaid details={providerDetails} />;
      case "INSURANCE":
        return <InsuranceDetails details={providerDetails} />;
      case "EDUCATION":
        return <EducationDetails details={providerDetails} />;
      // DATA, AIRTIME, TV — no extra detail needed
      default:
        return null;
    }
  };

  const content = renderContent();
  if (!content) return null;

  return (
    <Box w="100%">
      {productName && <DetailRow label="Product" value={productName} />}
      {content}
    </Box>
  );
};
